import { Box, Checkbox, CircularProgress, FormControlLabel, IconButton, Tooltip } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { colors } from 'styles/colors';
import BlockIcon from '@mui/icons-material/Block';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import CustomDialog from 'components/dialogs/CustomDialog';
import { useBlockUser, useUnblockUser } from 'hooks/useUsers';
import { snackbarService } from 'services/snackbarService';
import { getErrorMessage } from 'common/utils';
import { IUser } from 'types/models/User';
import { ERoleType, EUserStatus } from 'enum';
import { useGetUserDevices } from 'hooks/useDevices';
import CustomText from 'components/common/CustomText/CustomText';
import { formatDateTime, toTitleCase } from 'common/utils';

interface Props {
  refetch?: () => void;
  user: IUser;
}

export default function BlockUserActionButton({ refetch, user }: Props) {
  const [open, setOpen] = useState(false);

  const isBlocked = useMemo(() => {
    return user.status === EUserStatus.BLOCKED;
  }, [user]);

  const { mutate: blockMutation, isLoading: isBlocking } = useBlockUser(
    (res: any) => {

      refetch?.();
      snackbarService.showSnackbar('User has been blocked successfully', 'success');
      setOpen(false);
    },
    (error: any) => {
      snackbarService.showSnackbar(getErrorMessage(error), 'error');
      setOpen(false);
    }
  );

  const { mutate: unblockMutation, isLoading: isUnBlocking } = useUnblockUser(
    (res: any) => {
      refetch?.();
      snackbarService.showSnackbar('User has been unblocked successfully', 'success');
      setOpen(false);
    },
    (error: any) => {
      snackbarService.showSnackbar(getErrorMessage(error), 'error');
      setOpen(false);
    }
  );

  // The phones the account was used on, read when blocking is about to be
  // confirmed. A phone only this account used is ticked; one shared with
  // other accounts is left for the admin to tick, since it may be theirs.
  const { data: devices, isLoading: isLoadingDevices } = useGetUserDevices(user.id, {
    enabled: open && !isBlocked,
  });
  const [deviceIds, setDeviceIds] = useState<string[]>([]);
  useEffect(() => {
    if (!devices) return;
    setDeviceIds(devices
      .filter((device: any) => device.accounts.every((account: any) => account.id === user.id))
      .map((device: any) => device.id));
  }, [devices, user.id]);

  const toggleDevice = (id: string) => {
    setDeviceIds((ids) => ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  };

  const handleConfirm = () => {
    isBlocked ? unblockMutation(user.id) : blockMutation({ endUserId: user.id, deviceIds });
  };

  const devicesContent = isBlocked ? undefined : (
    <Box mt={2}>
      <CustomText text='Also block these phones:' fontSize={15} fontWeight={600} />
      {isLoadingDevices ? (
        <Box mt={1}><CircularProgress size={20} /></Box>
      ) : !devices?.length ? (
        <CustomText text='No phones recorded for this account.' fontSize={14} color={colors.gray} />
      ) : (
        devices.map((device: any) => {
          const others = device.accounts.filter((account: any) => account.id !== user.id);
          return (
            <Box key={device.id} mt={1}>
              <FormControlLabel
                control={<Checkbox size='small' checked={deviceIds.includes(device.id)} onChange={() => toggleDevice(device.id)} />}
                label={`${[device.brand, device.model].filter(Boolean).join(' ') || 'Unknown phone'} - last seen ${formatDateTime(device.lastSeenAt)}${device.blockedAt ? ' (already blocked)' : ''}`}
              />
              {others.length > 0 && (
                <Box pl={4}>
                  <CustomText
                    text={`Also used by: ${others.map((account: any) => toTitleCase(account.fullName || '—')).join(', ')}`}
                    fontSize={13}
                    color={colors.warning}
                  />
                </Box>
              )}
            </Box>
          );
        })
      )}
    </Box>
  );

  // Admins cannot be blocked - the server refuses it - so there is nothing to
  // offer. This covers the signed-in admin too.
  if (user.role === ERoleType.ADMIN) return null;

  return (
    <>
      <Tooltip title={isBlocked ? 'Unblock' : 'Block'}>
        <IconButton
          onClick={() => setOpen(true)}
          sx={{
            width: 25,
            height: 25,
            backgroundColor: isBlocked ? colors.success : colors.error,
            borderRadius: '4px',
            padding: 0,
            '&:hover': {
              backgroundColor: isBlocked ? colors.success : colors.delete,
            },
          }}
        >
          <BlockIcon sx={{ fontSize: 15, color: colors.white }} />
        </IconButton>
      </Tooltip>

      <CustomDialog
        open={open}
        title={`${isBlocked ? 'Unblock' : 'Block'} user`}
        message={`Are you sure you want to ${isBlocked ? 'unblock' : 'block'} ${user.fullName}?`}
        content={devicesContent}
        buttons={[
          <CustomButton
            key='cancel'
            variant='outlined'
            text='Cancel'
            onClick={() => setOpen(false)}
            disabled={isUnBlocking || isBlocking}
            height={35}
          />,
          <CustomButton
            key='block'
            variant='filled'
            color={isBlocked ? 'success' : 'error'}
            text={isBlocked ? 'Unblock' : 'Block'}
            onClick={handleConfirm}
            loading={isUnBlocking || isBlocking}
            disabled={!isBlocked && isLoadingDevices}
            height={35}
          />,
        ]}
      />
    </>
  );
}
